import type { PriceParameters } from "./types";

/**
 * Fallback for a parameter that has no centrally stored value yet. These are
 * the values the price report has always fallen back to; changing one changes
 * calculated prices wherever no central value is stored.
 */
export const DEFAULT_PRICE_PARAMETERS: PriceParameters = {
  workCost: 17,
  pAlu: 0.1,
  pPanel: 0.15,
  pNKMat: 0.2,
  pNebenzeit: 0.2,
  pMarginMaterial: 1.15,
  pMarginTotal: 0,
  otherCost: 3,
};

export const PRICE_PARAMETER_KEYS = Object.keys(
  DEFAULT_PRICE_PARAMETERS,
) as (keyof PriceParameters)[];

/**
 * The only parameters a single position may deviate in. Everything else is
 * maintained centrally and applies to every position.
 */
export const POSITION_PRICE_PARAMETER_KEYS = [
  "otherCost",
  "pMarginMaterial",
  "pMarginTotal",
] as const satisfies readonly (keyof PriceParameters)[];

export type PositionPriceParameterKey =
  (typeof POSITION_PRICE_PARAMETER_KEYS)[number];

export type PositionPriceParameterOverrides = Partial<
  Pick<PriceParameters, PositionPriceParameterKey>
>;

function isValidParameterValue(value: unknown): value is number {
  return typeof value === "number" && Number.isFinite(value) && value >= 0;
}

/** Central parameter set from the stored entries, with defaults for gaps. */
export function resolvePriceParameters(
  entries:
    | Readonly<Record<string, { value: number } | undefined>>
    | null
    | undefined,
): PriceParameters {
  const params = { ...DEFAULT_PRICE_PARAMETERS };
  for (const key of PRICE_PARAMETER_KEYS) {
    const value = entries?.[key]?.value;
    if (isValidParameterValue(value)) {
      params[key] = value;
    }
  }
  return params;
}

/**
 * Reads the overrides stored on a position. Anything that is not an
 * overridable key with a valid number is ignored, so a position can never
 * change a centrally maintained parameter.
 */
export function parsePositionPriceOverrides(
  raw: unknown,
): PositionPriceParameterOverrides {
  const overrides: PositionPriceParameterOverrides = {};
  if (raw === null || typeof raw !== "object") {
    return overrides;
  }
  const record = raw as Record<string, unknown>;
  for (const key of POSITION_PRICE_PARAMETER_KEYS) {
    const value = record[key];
    if (isValidParameterValue(value)) {
      overrides[key] = value;
    }
  }
  return overrides;
}

export function applyPositionPriceOverrides(
  central: PriceParameters,
  overrides: PositionPriceParameterOverrides,
): PriceParameters {
  return { ...central, ...overrides };
}

/**
 * The overrides to store for `params`: only overridable keys that differ from
 * the central value, so a position without deviations follows later central
 * changes. `null` when nothing deviates.
 */
export function diffPositionPriceOverrides(
  central: PriceParameters,
  params: PriceParameters,
): PositionPriceParameterOverrides | null {
  const overrides: PositionPriceParameterOverrides = {};
  for (const key of POSITION_PRICE_PARAMETER_KEYS) {
    const value = params[key];
    if (isValidParameterValue(value) && value !== central[key]) {
      overrides[key] = value;
    }
  }
  return Object.keys(overrides).length > 0 ? overrides : null;
}
